import {useEffect, useState} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    Card,
    CardContent,
    Typography,
    IconButton,
    InputAdornment,
    Checkbox,
    Button,
    FormControlLabel,
    Divider,
    TextField,
    Box,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import logo from "../../assets/img/logo-full.png";
import './SignIn.css';

const loginSchema = z.object({
    username: z.string()
        .min(1, 'Username is required')
        .min(3, 'Username must be at least 3 characters'),
    password: z.string()
        .min(1, 'Password is required')
        .min(6, 'Password must be at least 6 characters')
});

type LoginFormInputs = z.infer<typeof loginSchema>;

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
        width: 450
    }
}));

// Decorative particles component
const FloatingParticles = () => (
    <div className="signin-particles">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="particle" />
        ))}
    </div>
);

const SignIn = () => {
    const navigate = useNavigate();
    const { login, isLoading, loginError, isAuthenticated } = useAuth();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError
    } = useForm<LoginFormInputs>({
        resolver: zodResolver(loginSchema),
        mode: 'onChange'
    });

    const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
        try {
            await login(data);
        } catch (error) {
            setError('root', {
                type: 'manual',
                message: 'Invalid credentials. Please try again.'
            });
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            const from = (location.state as any)?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, location]);

    return (
        <Box className="signin-container">
            <FloatingParticles />
            <StyledCard className="signin-card">
                <CardContent sx={{ p: 6 }}>
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <img 
                                src={logo} 
                                alt="Logo" 
                                className="signin-logo"
                                style={{ height: 100, maxWidth: '100%' }} 
                            />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography 
                                variant="h5" 
                                gutterBottom
                                className="signin-title"
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}
                            >
                                Welcome back!
                            </Typography>
                            <Typography 
                                color="textSecondary"
                                className="signin-subtitle"
                                sx={{ fontSize: '0.95rem' }}
                            >
                                Please sign in to your account
                            </Typography>
                        </Box>

                        {(errors.root || loginError) && (
                            <Alert 
                                severity="error" 
                                sx={{ mb: 3 }}
                                className="signin-alert"
                            >
                                {errors.root?.message || 'Invalid credentials. Please try again.'}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate className="signin-form">
                            <TextField
                                {...register('username')}
                                fullWidth
                                label="Email"
                                variant="outlined"
                                error={!!errors.username}
                                helperText={errors.username?.message}
                                sx={{ mb: 3 }}
                                className="signin-input"
                            />

                            <TextField
                                {...register('password')}
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() => setShowPassword(!showPassword)}
                                                sx={{
                                                    color: '#00CC00',
                                                    '&:hover': {
                                                        color: '#00FF00',
                                                        transform: 'scale(1.1)',
                                                        transition: 'all 0.3s ease'
                                                    }
                                                }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                                className="signin-input"
                            />

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3
                            }}>
                                <div />
                                <MuiLink
                                    component={RouterLink}
                                    to="/forgot-password"
                                    className="signin-link"
                                >
                                    Forgot password?
                                </MuiLink>
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                type="submit"
                                disabled={isLoading}
                                sx={{ mb: 3 }}
                                className="signin-button"
                            >
                                {isLoading ? 'Signing in...' : 'Sign in'}
                            </Button>

                            <Divider sx={{ mb: 3 }} className="signin-divider"></Divider>
                        </form>
                    </>
                </CardContent>
            </StyledCard>
        </Box>
    );
};

export default SignIn;
